import type { PlayResult } from '../sim/index.js';

/**
 * The result.
 *
 * It renders a `PlayResult` and computes nothing. Accuracy and the average
 * offset are already numbers on that object, worked out once in `PlayResult.ts`
 * — a screen that recomputed them would be a second definition, and the first
 * time the two disagreed the player would be told two different things about
 * the same run.
 *
 * The panel is deliberately narrow and offset rather than full-bleed: the
 * mandala keeps turning behind it, which is the phase's requirement that the
 * world survive the transition. Waves are gone by the time this appears — that
 * is enforced by the runtime, not by drawing over them.
 */

const STYLE_ID = 'mandalizm-result-style';

const CSS = `
.mandalizm-result {
  position: fixed; inset: 0; z-index: 55; display: grid; place-items: center;
  padding: 20px; box-sizing: border-box; overflow-y: auto;
  /* Softer than the other overlays: the point is to keep the world visible. */
  background: radial-gradient(circle at 50% 50%, rgba(0,6,10,0.42), rgba(0,2,6,0.86));
  font: 13px/1.7 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; color: #bff4ee;
  transition: opacity 400ms ease;
}
.mandalizm-result[hidden] { display: none; }
.mandalizm-result--leaving { opacity: 0; pointer-events: none; }
.mandalizm-result__panel {
  width: min(520px, 100%); max-height: calc(100dvh - 40px); overflow-y: auto;
  padding: 24px 26px; box-sizing: border-box;
  background: rgba(0, 10, 16, 0.82);
  border: 1px solid rgba(90, 240, 220, 0.28); border-radius: 10px;
  box-shadow: 0 12px 60px rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
}
.mandalizm-result__heading {
  font-size: 11px; letter-spacing: 0.3em; color: rgba(140, 205, 220, 0.6); margin-bottom: 10px;
}
.mandalizm-result__song { font-size: 20px; color: #eafeff; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.mandalizm-result__meta {
  color: #7fe9dc; font-size: 11px; letter-spacing: 0.1em; margin-bottom: 18px;
}
.mandalizm-result__headline {
  display: flex; gap: 26px; flex-wrap: wrap; align-items: flex-end; margin-bottom: 18px;
}
.mandalizm-result__score b {
  display: block; font-size: 34px; color: #eafeff; font-weight: 500;
  font-variant-numeric: tabular-nums; line-height: 1.1;
}
.mandalizm-result__score span { font-size: 10px; letter-spacing: 0.22em; color: rgba(140, 205, 220, 0.6); }
.mandalizm-result__accuracy b {
  display: block; font-size: 26px; color: #7ef5cf; font-weight: 500;
  font-variant-numeric: tabular-nums; line-height: 1.1;
}
.mandalizm-result__accuracy span { font-size: 10px; letter-spacing: 0.22em; color: rgba(140, 205, 220, 0.6); }
.mandalizm-result__grades { display: grid; gap: 4px; margin-bottom: 16px; }
.mandalizm-result__grade {
  display: grid; grid-template-columns: 1fr auto; align-items: baseline;
  padding: 5px 9px; border-radius: 5px; background: rgba(4, 26, 33, 0.6);
}
.mandalizm-result__grade b { font-variant-numeric: tabular-nums; font-weight: 600; color: #eafeff; }
.mandalizm-result__grade--perfect { color: #ffe9ae; }
.mandalizm-result__grade--great { color: #a8f0d8; }
.mandalizm-result__grade--good { color: #9fd8dd; }
.mandalizm-result__grade--bad { color: #ffb38a; }
.mandalizm-result__grade--miss { color: #ff9ab4; }
.mandalizm-result__timing {
  display: flex; gap: 20px; flex-wrap: wrap; margin-bottom: 20px;
  padding-top: 14px; border-top: 1px solid rgba(90, 240, 220, 0.14);
  font-size: 11px; color: rgba(150, 215, 225, 0.7);
}
.mandalizm-result__timing b {
  display: block; color: #eafeff; font-size: 15px; font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.mandalizm-result__actions { display: flex; gap: 8px; flex-wrap: wrap; }
.mandalizm-result__action {
  appearance: none; cursor: pointer; font: inherit; font-size: 11px; letter-spacing: 0.16em;
  flex: 1 1 auto; padding: 12px 16px; color: #b6e8e4;
  background: rgba(3, 30, 38, 0.85); border: 1px solid rgba(90, 240, 220, 0.26);
  border-radius: 6px; transition: background 140ms ease, color 140ms ease;
}
.mandalizm-result__action:hover:not(:disabled) { background: rgba(6, 70, 78, 0.95); color: #eafeff; }
.mandalizm-result__action--primary {
  color: #eafeff; border-color: rgba(120, 250, 232, 0.6); background: rgba(4, 60, 66, 0.9);
}
.mandalizm-result__action:disabled { opacity: 0.4; cursor: progress; }
.mandalizm-result__action:focus-visible { outline: 2px solid #b9fff2; outline-offset: 3px; }
@media (max-width: 430px), (max-height: 540px) {
  .mandalizm-result { padding: 12px; }
  .mandalizm-result__panel { padding: 18px; max-height: calc(100dvh - 24px); }
  .mandalizm-result__actions { position: sticky; bottom: -18px; padding: 10px 0; background: #000a10; }
  .mandalizm-result__action { padding: 12px 10px; font-size: 10px; letter-spacing: 0.08em; }
}
`;

export interface ResultHandlers {
  readonly onDemoSelect?: () => void | Promise<void>;
  readonly onNext?: () => void | Promise<void>;
  readonly onRetry: () => void | Promise<void>;
  readonly onSongSelect: () => void | Promise<void>;
  readonly onTitle: () => void | Promise<void>;
}

export class ResultScreen {
  private readonly demoSelectButton: HTMLButtonElement;
  private readonly nextButton: HTMLButtonElement;
  private readonly nextStatus: HTMLDivElement;
  private nextState: 'loading' | 'ready' | 'error' = 'loading';
  private busy = false;
  readonly element: HTMLDivElement;

  private readonly song: HTMLDivElement;
  private readonly heading: HTMLDivElement;
  private readonly retryButton: HTMLButtonElement;
  private readonly selectButton: HTMLButtonElement;
  private readonly meta: HTMLDivElement;
  private readonly score: HTMLDivElement;
  private readonly accuracy: HTMLDivElement;
  private readonly grades: HTMLDivElement;
  private readonly timing: HTMLDivElement;
  private readonly actions: HTMLButtonElement[] = [];

  constructor(handlers: ResultHandlers, parent: HTMLElement = document.body) {
    injectStyle();
    this.element = document.createElement('div');
    this.element.className = 'mandalizm-result';
    this.element.hidden = true;

    const panel = document.createElement('div');
    panel.className = 'mandalizm-result__panel';

    this.heading = document.createElement('div');
    this.heading.className = 'mandalizm-result__heading';
    this.heading.textContent = 'RESULT';

    this.song = document.createElement('div');
    this.song.className = 'mandalizm-result__song';
    this.meta = document.createElement('div');
    this.meta.className = 'mandalizm-result__meta';

    const headline = document.createElement('div');
    headline.className = 'mandalizm-result__headline';
    this.score = document.createElement('div');
    this.score.className = 'mandalizm-result__score';
    this.accuracy = document.createElement('div');
    this.accuracy.className = 'mandalizm-result__accuracy';
    headline.append(this.score, this.accuracy);

    this.grades = document.createElement('div');
    this.grades.className = 'mandalizm-result__grades';
    this.timing = document.createElement('div');
    this.timing.className = 'mandalizm-result__timing';

    const actions = document.createElement('div');
    actions.className = 'mandalizm-result__actions';
    this.retryButton = action('RETRY', true);
    this.retryButton.addEventListener('click', () => void handlers.onRetry());
    this.selectButton = action('SONG SELECT');
    this.selectButton.addEventListener('click', () => void handlers.onSongSelect());
    const title = action('TITLE');
    title.addEventListener('click', () => void handlers.onTitle());
    this.actions.push(this.retryButton, this.selectButton, title);
    actions.append(this.retryButton, this.selectButton, title);
    this.nextButton = action('NEXT TRACK');
    this.nextButton.hidden = true;
    this.nextButton.addEventListener('click', () => void handlers.onNext?.());
    actions.append(this.nextButton);
    this.demoSelectButton = action('DEMO SELECT');
    this.demoSelectButton.hidden = true;
    this.demoSelectButton.addEventListener('click', () => void handlers.onDemoSelect?.());
    this.actions.push(this.demoSelectButton);
    actions.append(this.demoSelectButton);
    this.nextStatus = document.createElement('div');
    this.nextStatus.setAttribute('role', 'status');
    this.nextStatus.style.cssText = 'font-size:11px;overflow-wrap:anywhere;margin-top:8px;';

    panel.append(this.heading, this.song, this.meta, headline, this.grades, this.timing, actions, this.nextStatus);
    this.element.appendChild(panel);
    parent.appendChild(this.element);
  }

  get visible(): boolean {
    return !this.element.hidden;
  }

  /** Changes only the labels; the runtime retains ownership of navigation. */
  setDemo(demo: boolean): void {
    this.demoSelectButton.hidden = !demo;
    this.nextButton.hidden = !demo;
    this.nextStatus.hidden = !demo;
    this.heading.textContent = demo ? 'DEMO COMPLETE' : 'RESULT';
    this.retryButton.textContent = demo ? 'PLAY AGAIN' : 'RETRY';
    this.selectButton.textContent = demo ? 'CUSTOM PLAY' : 'SONG SELECT';
  }

  /** Draws a finished run. Reads the value and derives nothing. */
  setResult(result: PlayResult): void {
    this.song.textContent = result.displayName;
    this.song.title = result.displayName;
    this.meta.textContent =
      `${result.difficulty.toUpperCase()} · ${formatSpeed(result.waveSpeed)} · ` +
      `${result.totalEvents.toLocaleString()} NOTES`;

    this.score.replaceChildren(strong(result.score.toLocaleString()), caption('SCORE'));
    this.accuracy.replaceChildren(strong(formatAccuracy(result.accuracy)), caption('ACCURACY'));

    this.grades.replaceChildren(
      gradeRow('PERFECT', result.perfect, 'perfect'),
      gradeRow('GREAT', result.great, 'great'),
      gradeRow('GOOD', result.good, 'good'),
      gradeRow('BAD', result.bad, 'bad'),
      gradeRow('MISS', result.miss, 'miss'),
    );

    this.timing.replaceChildren(
      stat('MAX COMBO', result.maxCombo.toLocaleString()),
      stat('EARLY', result.early.toLocaleString()),
      stat('LATE', result.late.toLocaleString()),
      stat('AVG OFFSET', formatOffset(result.averageOffsetMs, result.tappedJudgements)),
    );
  }

  show(): void {
    this.element.hidden = false;
    this.element.classList.remove('mandalizm-result--leaving');
  }

  hide(): void {
    this.element.classList.add('mandalizm-result--leaving');
    window.setTimeout(() => {
      if (this.element.classList.contains('mandalizm-result--leaving')) {
        this.element.hidden = true;
      }
    }, 420);
  }

  setBusy(busy: boolean): void {
    this.busy = busy;
    this.nextButton.disabled = busy || this.nextState === 'loading';
    for (const control of this.actions) control.disabled = busy;
  }

  setNextState(state: 'loading' | 'ready' | 'error', error = ''): void {
    this.nextState = state;
    this.nextButton.disabled = this.busy || state === 'loading';
    this.nextStatus.textContent = state === 'loading' ? 'Preparing next track…'
      : state === 'error' ? `Next track unavailable. Press NEXT TRACK to retry. ${error}` : '';
  }

  dispose(): void {
    this.element.remove();
  }
}

// ---------------------------------------------------------------------------

function strong(text: string): HTMLElement {
  const element = document.createElement('b');
  element.textContent = text;
  return element;
}

function caption(text: string): HTMLElement {
  const element = document.createElement('span');
  element.textContent = text;
  return element;
}

function gradeRow(label: string, count: number, modifier: string): HTMLDivElement {
  const row = document.createElement('div');
  row.className = `mandalizm-result__grade mandalizm-result__grade--${modifier}`;
  const name = document.createElement('span');
  name.textContent = label;
  row.append(name, strong(count.toLocaleString()));
  return row;
}

function stat(label: string, value: string): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.append(strong(value), document.createTextNode(label));
  return wrapper;
}

export function formatAccuracy(accuracy: number): string {
  if (!Number.isFinite(accuracy)) return '—';
  return `${(accuracy * 100).toFixed(2)}%`;
}

/**
 * Signed, so the sign carries the meaning: negative is early.
 *
 * A run with no taps at all shows a dash rather than `0 ms`, because zero would
 * read as perfect timing when it actually means there was no timing.
 */
export function formatOffset(averageMs: number, tapped: number): string {
  if (tapped <= 0 || !Number.isFinite(averageMs)) return '—';
  const rounded = Math.round(averageMs);
  return `${rounded > 0 ? '+' : ''}${rounded} ms`;
}

function formatSpeed(value: number): string {
  return `${Number(value.toFixed(2))}x`;
}

function action(text: string, primary = false): HTMLButtonElement {
  const element = document.createElement('button');
  element.className = primary
    ? 'mandalizm-result__action mandalizm-result__action--primary'
    : 'mandalizm-result__action';
  element.type = 'button';
  element.textContent = text;
  return element;
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
